import { FieldCellValueUI, FormFieldValuesUI, LensHit } from "Backend";
import { StateHandle } from "StateHandle";
import React, { useContext, useEffect, useState } from "react";
import { nonEmpty } from "helpers/coll";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useSnackbar } from "notistack";
import { CaseFormContext } from "contexts/CaseFormContext";
import { selectForm } from "redux/slices/form.slice";
import { putHitInForm } from "redux/slices/lensRun";
import { BroadcastChannelContext } from "contexts/BroadcastChannelContext";
import { FieldCellValue, Form, UpsertFullValueGroupsDocument, ValueGroupToAddInput } from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { validateCell } from "helpers/FormHelpers";

export interface PreviousFormCardState {
  formGroupValuesState: FieldCellValue[];
  pageRangeState: [number, number] | undefined;
  fieldAnsweredState: boolean | undefined | null;
  notes: string | undefined;
}

export interface UseFormCard {
  formModeState: StateHandle<boolean>;
  form?: Form;
  serverValues: FormFieldValuesUI[];
  formGroupValuesState: StateHandle<FieldCellValueUI[]>;
  savingFormState: StateHandle<boolean>;
  fieldIdState: StateHandle<number | undefined>;
  fieldAnsweredState: StateHandle<boolean | undefined>;
  pageRangeState: StateHandle<[number, number] | undefined>;
  saveFormValue: (extraReferenceIndices?: number[], showSnackbar?: boolean) => Promise<void>;
  toggleFormMode: () => Promise<void>;
  getValueGroupToAdd: (
    extraReferenceIndices: number[],
    yesNoValueState: boolean | undefined
  ) => ValueGroupToAddInput | undefined;
  isFormValid: boolean;
  canSave: boolean;
  selectedValueGroupState: StateHandle<number>;
  intakePage: number;
  lensId: number;
  caseId: number;
  multipleReferencesState: StateHandle<boolean>;
  notesHandle: StateHandle<string | undefined>;
}

interface UseFormCardProps {
  lensId: number;
  caseId: number;
  intakeId: number;
  intakePage: number;
  blockIndex?: number | null;
  initialFieldId?: number;
  hit?: LensHit | undefined;
  previousState?: PreviousFormCardState | undefined;
}

export function useFormCard(props: UseFormCardProps): UseFormCard {
  const { lensId, caseId, intakeId, intakePage, blockIndex, initialFieldId, hit, previousState } = props;
  const broadcastChannelMap = useContext(BroadcastChannelContext);
  const caseForm = useContext(CaseFormContext);
  const { enqueueSnackbar } = useSnackbar();
  const form: Form | undefined = useAppSelector(selectForm);
  const formId = form?.id;
  const formModeState = React.useState(false);
  const [formMode, setFormMode] = formModeState;
  const savingFormState = React.useState(false);
  const [savingForm, setSavingForm] = savingFormState;
  const fieldAnsweredState = React.useState<boolean | undefined>(previousState?.fieldAnsweredState || undefined);
  const multipleReferencesState = React.useState<boolean>(false);
  const pageRangeState = React.useState<[number, number] | undefined>(previousState?.pageRangeState);
  const [pageRange, _setPageRange] = pageRangeState;
  const notesHandle = useState<string | undefined>(previousState?.notes || "");
  const [usedPreviousState, setUsedPreviousState] = useState<boolean>(false);
  const formGroupValuesState = React.useState<FieldCellValueUI[]>(previousState?.formGroupValuesState || []);
  useEffect(() => {
    if (previousState !== undefined && !usedPreviousState) {
      formGroupValuesState[1](previousState.formGroupValuesState);
      setUsedPreviousState(true);
    }
  }, [previousState?.formGroupValuesState, usedPreviousState]);
  const fieldIdState = React.useState<number | undefined>(initialFieldId || form?.fields[0]?.id);
  const [fieldId, setFieldId] = fieldIdState;
  const selectedValueGroupState = React.useState(-1);
  const dispatch = useAppDispatch();

  const getValueGroupToAdd = (
    extraReferenceIndices: number[] = [],
    yesNoValueState: boolean | undefined
  ): ValueGroupToAddInput | undefined => {
    const allReferenceIndices = [...new Set([blockIndex].concat(extraReferenceIndices))];
    const valueGroupCells = formGroupValuesState[0];
    const thisFieldValues = caseForm.serverValuesForField(fieldId);
    // If the answer is "no", ignore the value fields
    const existingValueGroup =
      selectedValueGroupState[0] === -1
        ? undefined
        : thisFieldValues?.valueGroups.find((vg) => vg.id === selectedValueGroupState[0]);
    const existingId = existingValueGroup?.id;
    const meaningfulPageRange = pageRange && pageRange[0] !== pageRange[1] ? pageRange : undefined;

    return yesNoValueState === false
      ? undefined
      : {
          blockIntakeReferences: allReferenceIndices.map((bIndex) => ({
            intakeId: intakeId,
            intakePage: intakePage,
            blockIndex: bIndex,
            pageRangeStart: meaningfulPageRange ? meaningfulPageRange[0] : undefined,
            pageRangeEnd: meaningfulPageRange ? meaningfulPageRange[1] : undefined,
          })),
          existingIndex: existingId,
          cells: valueGroupCells.map((c) => c.value),
          notes: (notesHandle[0] || "").trim() || undefined,
          selectionStatus: existingValueGroup?.selectionStatus,
        };
  };

  useEffect(() => {
    if (fieldId === undefined && form && form.fields[0] !== undefined) {
      setFieldId(form.fields[0].id);
    }
  }, [form?.fields.length]);

  const saveFormValue = async (extraReferenceIndices: number[] = []) => {
    if (!intakeId) return;
    if (!form) return;
    if (fieldId === undefined) return;
    setSavingForm(true);
    const valueGroupCells = formGroupValuesState[0];
    const yesNoValueState = fieldAnsweredState[0];
    const valueGroup: ValueGroupToAddInput | undefined = getValueGroupToAdd(extraReferenceIndices, yesNoValueState);

    // If yesNoValue is set, use it, Otherwise default to true if cells are non-empty (for repeaters).
    const yesNoValue = yesNoValueState !== undefined ? yesNoValueState : nonEmpty(valueGroupCells);
    try {
      await runGqlMutation(UpsertFullValueGroupsDocument, {
        formId: formId!,
        caseId,
        fieldId,
        yesNoValue,
        valueGroups: valueGroup !== undefined ? [valueGroup] : undefined,
      });
      await caseForm.resetAllFieldsServer();
      broadcastChannelMap?.pin?.postMessage(caseId);
      setSavingForm(false);
      setFormMode(false);
      if (hit) dispatch(putHitInForm({ hitId: hit.hitId }));
      enqueueSnackbar("Successfully saved profile data", {
        variant: "success",
      });
    } catch (e) {
      enqueueSnackbar("Unable to save profile data", {
        variant: "error",
      });
      throw e;
    }
  };

  const toggleFormMode = async () => {
    if (formMode) {
      return saveFormValue();
    } else {
      notesHandle[1]("");
      setFormMode(true);
    }
  };

  const isFormValid = (() => {
    if (!form) return false;
    const field = form.fields.find((f) => f.id === fieldId);
    if (!field) return false;

    const yesNoValue = fieldAnsweredState[0];
    const serverField = caseForm.serverFieldValues.find((v) => v.fieldId === field.id);
    const serverYesNoValue = serverField?.yesNoValue;

    if (field.promptYesNo && !field.repeating && yesNoValue === undefined) {
      return false;
    }

    // You can save a "no" to a non-repeating question if and only if it
    // has not been answered previously. You can't 'delete' answers from the card
    if (yesNoValue === false && !field.repeating) {
      return serverYesNoValue !== true;
    }

    const valueGroup = formGroupValuesState[0];
    if (field.cells.length !== valueGroup.length) {
      return false;
    }
    return valueGroup.every((v, idx) => {
      const cell = field.cells[idx];
      if (cell !== undefined) {
        return validateCell(v, cell);
      }
      return false;
    });
  })();

  const canSave = !savingForm && isFormValid;

  return {
    formModeState,
    form,
    serverValues: caseForm.serverFieldValues,
    formGroupValuesState,
    savingFormState,
    fieldIdState,
    fieldAnsweredState,
    pageRangeState,
    saveFormValue,
    getValueGroupToAdd,
    toggleFormMode,
    isFormValid,
    canSave,
    selectedValueGroupState,
    intakePage,
    lensId,
    caseId,
    multipleReferencesState,
    notesHandle,
  };
}
